import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { NavigationEnd, Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map, startWith } from 'rxjs/operators';
import { PropertyStore } from '../../services/property.store';
import { WorkspaceStore } from '../../services/workspace.store';
import { UIFeedbackService } from '../../services/ui-feedback.service';
import {
  filterGracePeriodProperties,
  gracePeriodMessage
} from '@shared/utils/grace-period.util';
import { isWorkspaceCompActive } from '@shared/utils/workspace-comp';

/**
 * Critical, app-wide banner: at least one property is scheduled for deletion
 * because no payment method is on file. Promoted out of the (no longer primary)
 * financial dashboard into the global banner stack so it is impossible to miss
 * — the founder's explicit requirement. It is non-dismissible and stays visible
 * the whole time the workspace is at risk. The banner stack offsets the toolbar
 * and content (via `--vex-global-banner-height`), so it never covers the
 * toolbar — see GlobalBannerOffsetDirective.
 */
@Component({
  selector: 'app-billing-grace-period-banner',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    TranslatePipe
  ],
  template: `
    @if (visible()) {
      <div class="grace-banner" role="alert">
        <div class="banner-inner">
          <span class="leading">
            <span class="dot" aria-hidden="true">
              <mat-icon>warning</mat-icon>
            </span>
            <span class="text">
              <strong>{{ 'dashboard.property.alerts.paymentRequired.title' | translate }}</strong>
              <span class="message">{{ message() }}</span>
              <span class="props">
                @for (p of properties(); track p.propertyUID) {
                  <span class="prop">
                    {{ p.name }}
                    @if (p.billingGracePeriodEnds) {
                      <span class="prop-date">
                        ({{ 'dashboard.property.alerts.expires' | translate }}:
                        {{ p.billingGracePeriodEnds | date: 'short' }})
                      </span>
                    }
                  </span>
                }
              </span>
            </span>
          </span>

          <span class="actions">
            <button
              type="button"
              mat-flat-button
              class="primary-action"
              (click)="openPaymentDialog()"
            >
              <mat-icon>credit_card</mat-icon>
              <span class="action-label">{{ 'dashboard.property.actions.addPaymentMethod' | translate }}</span>
            </button>
          </span>
        </div>
      </div>
    }
  `,
  styles: [`
    :host {
      display: block;
      width: 100%;
    }

    .grace-banner {
      width: 100%;
      box-sizing: border-box;
      /* Critical-styled. Positioning is owned by the global banner stack
         wrapper (fixed, high z-index); this element just fills its width. */
      background: rgb(var(--ds-danger) / 0.12);
      border-bottom: 2px solid rgb(var(--ds-danger));
      box-shadow: 0 2px 6px rgba(153, 27, 27, 0.18);
    }

    .banner-inner {
      max-width: 1280px;
      margin: 0 auto;
      padding: 8px 16px;
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .leading {
      display: inline-flex;
      align-items: center;
      gap: 10px;
      flex: 1;
      min-width: 0;
    }

    .dot {
      width: 28px;
      height: 28px;
      border-radius: 50%;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: rgb(var(--ds-danger));
      color: rgb(var(--ds-surface));
      flex-shrink: 0;

      mat-icon {
        font-size: 18px;
        width: 18px;
        height: 18px;
      }
    }

    .text {
      display: flex;
      align-items: baseline;
      gap: 8px;
      flex-wrap: wrap;
      min-width: 0;

      strong {
        font-weight: 700;
        font-size: 13px;
        color: rgb(var(--ds-danger));
      }

      .message {
        font-size: 12px;
        color: rgb(var(--ds-danger));
        line-height: 1.4;
        font-weight: 600;
      }
    }

    .props {
      display: inline-flex;
      flex-wrap: wrap;
      gap: 8px;
      font-size: 12px;
      color: rgb(var(--ds-danger));
    }

    .prop {
      white-space: nowrap;

      .prop-date {
        color: rgb(var(--ds-danger));
        opacity: 0.85;
      }
    }

    .actions {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      flex-shrink: 0;
    }

    .primary-action {
      background: rgb(var(--ds-danger)) !important;
      color: rgb(var(--ds-surface)) !important;
      font-weight: 600 !important;
      border-radius: 6px !important;
      font-size: 12px !important;
      padding: 4px 12px !important;
      min-height: 30px !important;
      line-height: 1 !important;
    }

    @media (max-width: 768px) {
      .banner-inner {
        flex-direction: column;
        align-items: stretch;
        gap: 8px;
        padding: 10px 14px;
      }

      .text {
        flex-direction: column;
        gap: 2px;
      }

      .actions {
        justify-content: flex-end;
      }

      .action-label {
        display: inline;
      }
    }
  `]
})
export class BillingGracePeriodBannerComponent {
  private propertyStore = inject(PropertyStore);
  private workspaceStore = inject(WorkspaceStore);
  private router = inject(Router);
  private translate = inject(TranslateService);
  private readonly uiFeedback = inject(UIFeedbackService);
  private readonly dialog = inject(MatDialog);

  /** Suppressed on auth routes (login/register) — same as the email banner. */
  private suppressed = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(e => this.isSuppressedUrl(e.urlAfterRedirects)),
      startWith(this.isSuppressedUrl(this.router.url))
    ),
    { initialValue: this.isSuppressedUrl(this.router.url) }
  );

  /**
   * Properties at risk. A workspace with a card on file (billing mirror
   * `hasPaymentMethod`) must never see this, even if a per-property flag is
   * briefly stale before the backend heals it on card-add.
   */
  readonly properties = computed(() => {
    const hasPaymentMethod = !!this.workspaceStore.currentWorkspace()?.billing?.hasPaymentMethod;
    if (hasPaymentMethod) return [];
    return filterGracePeriodProperties(this.propertyStore.list(), new Date());
  });

  readonly visible = computed(() => !this.suppressed() && this.properties().length > 0);

  readonly message = computed(() => {
    const { key, params } = gracePeriodMessage(this.properties(), new Date());
    return this.translate.instant(key, params);
  });

  private isSuppressedUrl(url: string): boolean {
    if (!url) return false;
    return (
      url.startsWith('/auth') ||
      url.startsWith('/login') ||
      url.startsWith('/register')
    );
  }

  /**
   * Open the Stripe card dialog straight from the banner.
   *
   * This used to navigate to `/apps/workspace/settings/billing`, which was
   * wrong twice over: it dropped the user on a page where they still had to
   * hunt for a second button (the label promises the card form), and it was a
   * silent no-op when they were already on that page — the reported "the button
   * does nothing". It also hardcoded the property-manager route, so an agency
   * workspace (whose billing page lives at `/agency/settings/billing`) landed
   * on the wrong page entirely. Opening the dialog in place sidesteps all of
   * it: `PaymentSetupDialogData` only needs a workspace id + name, both of
   * which we already read for `properties()`.
   *
   * Nothing to do on close — the backend clears `requiresPaymentSetup` /
   * `billingGracePeriodEnds` on every property once a card attaches (see
   * stripe/gracePeriodFlags.ts), so `properties()` empties itself through the
   * Firestore listeners and the banner disappears on its own.
   */
  async openPaymentDialog(): Promise<void> {
    const workspace = this.workspaceStore.currentWorkspace();
    if (!workspace) return;

    // Same two pre-conditions the billing page applies before opening this
    // dialog (workspace-billing.component.ts) — kept in sync deliberately.
    if (!(await this.workspaceStore.hasRole(['owner', 'admin']))) {
      this.uiFeedback.toast.info(
        this.translate.instant('workspace.billing.unauthorizedManage')
      );
      return;
    }

    // Complimentary workspaces have no Stripe customer and never pay; a card
    // would be meaningless.
    if (isWorkspaceCompActive(workspace.comp)) {
      this.uiFeedback.toast.info(
        this.translate.instant('workspace.billing.comp.noPaymentNeeded')
      );
      return;
    }

    // Lazy: this banner is mounted app-wide, and the dialog pulls in
    // @stripe/stripe-js. A static import would drag Stripe into the initial
    // bundle for every user, the vast majority of whom never see this banner.
    const { PaymentSetupDialogComponent } = await import(
      '@app/pages/apps/workspace/workspace-settings/payment-setup-dialog/payment-setup-dialog.component'
    );

    this.dialog.open(PaymentSetupDialogComponent, {
      panelClass: ['dialog-size-medium'],
      data: {
        workspaceId: workspace.workspaceId,
        workspaceName: workspace.name
      }
    });
  }
}
